import { Button } from "@admin/components/atoms/Button";
import { ErrorMessage } from "@admin/components/atoms/ErrorMessage";
import { Input } from "@admin/components/atoms/Input";
import { Label } from "@admin/components/atoms/Input/Label";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import { addDish, Dish, updateDish, UpsertModalType } from "@enos-monorepo/lib";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { z } from "zod";

interface UpsertProcessingProductModalProps extends ModalProps {
  companyId?: string;
  selectedLocationId?: string;
  dish?: Dish;
  type: UpsertModalType;
}

export function UpsertProcessingProductModal({
  companyId,
  selectedLocationId,
  dish,
  type,
  ...props
}: UpsertProcessingProductModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const dishSchema = z.object({
    name: z.string().min(1, t("common.Name is required")),
  });

  type DishSchema = z.infer<typeof dishSchema>;

  const { mutate: addDishMutation, isPending: isAdding } = useMutation({
    mutationFn: addDish,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dishesByCompany"] });
      reset();
      props?.onClose?.();
      toast.success(t("food.Proccessing Product Has Been Added"));
    },
    onError: (err: Error) => {
      toast.error(t("food.Failed to add Proccessing Product"), {
        duration: 4000,
      });
    },
  });

  const { mutate: updateDishMutation, isPending: isUpdating } = useMutation({
    mutationFn: updateDish,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dishesByCompany"] });
      reset();
      props?.onClose?.();
      toast.success(t("food.Proccessing Product Has Been Updated"));
    },
    onError: (err: Error) => {
      toast.error(t("food.Failed to update Proccessing Product"), {
        duration: 4000,
      });
    },
  });

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
  } = useForm<DishSchema>({
    resolver: zodResolver(dishSchema),
  });

  useEffect(() => {
    if (dish) {
      reset({
        name: dish.name,
      });
    }
  }, [reset, dish]);

  const handleSaveClick = handleSubmit((data) => {
    if (!companyId || !selectedLocationId) return;

    if (type === UpsertModalType.Add) {
      addDishMutation({
        companyId,
        companyLocationId: selectedLocationId,
        name: data.name,
      });
      return;
    }

    updateDishMutation({
      companyId,
      companyLocationId: selectedLocationId,
      name: data.name,
      id: dish?.id,
    });
  });

  const handleOnClose = () => {
    reset();
    props.onClose?.();
  };

  useEffect(() => {
    if (dish) {
      reset(dish);
    }
  }, [reset, dish]);

  return (
    <Modal
      {...props}
      onClose={handleOnClose}
      actionButtons={
        <>
          <Button
            variant="secondary"
            onClick={handleOnClose}
            disabled={isAdding || isUpdating}
          >
            {t("common.Discard")}
          </Button>
          <Button onClick={handleSaveClick} loading={isAdding || isUpdating}>
            {t("food.Save Product")}
          </Button>
        </>
      }
      title={`${type === UpsertModalType.Add ? t("food.Add Processing Product") : t("food.Edit Processing Product")} `}
    >
      <form>
        <div className="flex flex-col">
          <Label title={t("food.Name of the Product")} required>
            <Input
              {...register("name")}
              name="name"
              type="text"
              defaultValue={dish?.name}
            />
          </Label>
          <ErrorMessage message={errors.name?.message} />
        </div>
      </form>
    </Modal>
  );
}
